import React from 'react';
import { Store, CheckCircle, MapPin, HelpingHand, Calendar, TrendingUp, Building } from 'lucide-react';

export default function MarketCenters() {
  const marketCenters = [
    { name: 'Atlanta Apparel', logo: 'https://logo.clearbit.com/atlanta-apparel.com', location: 'Atlanta, GA' },
    { name: 'Dallas Market Center', logo: 'https://logo.clearbit.com/dallasmarketcenter.com', location: 'Dallas, TX' },
    { name: 'Las Vegas Market', logo: 'https://logo.clearbit.com/lasvegasmarket.com', location: 'Las Vegas, NV' },
    { name: 'LA Mart', logo: 'https://logo.clearbit.com/lamart.com', location: 'Los Angeles, CA' },
    { name: 'AmericasMart Atlanta', logo: 'https://logo.clearbit.com/americasmart.com', location: 'Atlanta, GA' }
  ];

  const framework = [
    {
      phase: 'Phase 1: Market Center Strategy',
      duration: '2-3 weeks',
      activities: [
        'Market center selection based on product category and buyer demographics',
        'Showroom location analysis (permanent vs. temporary, floor level, traffic patterns)',
        'Competitive landscape mapping and differentiation strategy',
        'Budget modeling: rent, build-out, staffing, samples',
        'ROI projections and success metrics definition'
      ]
    },
    {
      phase: 'Phase 2: Showroom Design & Setup',
      duration: '4-6 weeks',
      activities: [
        'Showroom layout design optimized for product discovery',
        'Visual merchandising that tells your brand story',
        'Sample inventory curation and organization system',
        'Technology integration (iPad ordering, digital catalogs)',
        'Buyer appointment scheduling infrastructure'
      ]
    },
    {
      phase: 'Phase 3: Buyer Acquisition',
      duration: 'Ongoing',
      activities: [
        'Existing buyer network introductions and showroom tours',
        'Market week appointment filling (80%+ capacity target)',
        'Walking traffic capture and qualification',
        'Referral program with complementary showrooms',
        'Digital marketing driving market week attendance'
      ]
    },
    {
      phase: 'Phase 4: Sales Execution & Growth',
      duration: 'Ongoing',
      activities: [
        'Professional showroom staffing during market weeks',
        'Order writing and immediate follow-up',
        'Between-markets buyer communication and reorders',
        'Line reviews and seasonal product launches',
        'Performance tracking and optimization'
      ]
    }
  ];

  const differentiators = [
    {
      title: 'The Buyer Network Advantage',
  icon: HelpingHand,
      description: 'We maintain relationships with 2,000+ active buyers across market centers. Your showroom opens with warm introductions, not cold prospects hoping for walk-ins.'
    },
    {
      title: 'Showroom Optimization System',
      icon: Building,
      description: 'Strategic location selection, traffic-optimized layout, and visual merchandising that converts browsers into buyers. Average showroom conversion rate: 34% vs. industry standard 18%.'
    },
    {
      title: 'Professional Representation',
      icon: TrendingUp,
      description: 'Experienced sales representatives who understand wholesale dynamics, can articulate your value proposition, and close orders—not just answer questions.'
    },
    {
      title: 'Year-Round Engagement',
      icon: Calendar,
      description: "Market centers aren't just about market weeks. We maintain buyer relationships between markets through line reviews, trunk shows, and private appointments that drive consistent reorders."
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-7xl mx-auto px-6 py-16">
        {/* Header */}
        <div className="mb-16">
          <div className="flex items-center gap-4 mb-6">
            <div className="w-16 h-16 bg-gradient-to-br from-[#38A3A5] to-[#38A3A5] rounded-xl flex items-center justify-center">
              <Store size={32} className="text-white" />
            </div>
            <div>
              <h1 className="text-5xl font-bold text-[#000000]">
                Market Center Entry
              </h1>
              <p className="text-xl text-gray-600 mt-2">
                Access major wholesale market centers
              </p>
            </div>
          </div>
        </div>

        {/* Market Centers We Cover */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold mb-8 text-[#000000]">Major Market Centers We Operate In</h2>
          <div className="bg-gradient-to-br from-gray-50 to-white border-2 border-gray-200 rounded-2xl p-8">
            <p className="text-lg text-gray-700 mb-8">
              We maintain permanent showroom presence and buyer relationships across the nation's leading wholesale market centers, providing our brand partners with strategic access to regional and national retailers.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-6">
              {marketCenters.map((center, index) => (
                <div key={index} className="bg-white border-2 border-gray-200 rounded-xl p-6 hover:border-[#38A3A5] transition-all">
                  <div className="flex flex-col items-center text-center">
                    <div className="mb-4 h-16 flex items-center justify-center">
                      <img 
                        src={center.logo} 
                        alt={center.name}
                        className="h-12 w-auto object-contain"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'block';
                        }}
                      />
                      <div style={{display: 'none'}} className="w-12 h-12 bg-gray-200 rounded-lg flex items-center justify-center">
                        <MapPin size={24} className="text-gray-400" />
                      </div>
                    </div>
                    <h3 className="font-bold text-sm text-gray-800 mb-1">{center.name}</h3>
                    <p className="text-xs text-gray-600">{center.location}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* The Motif Unbound Framework */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold mb-4 text-[#000000]">The Strategic Market Center Framework</h2>
          <p className="text-lg text-gray-700 mb-8">
            Market centers represent massive wholesale opportunity—billions in annual orders flow through these buildings. But most brands fail because they treat showrooms like expensive storage units. We approach market centers as strategic sales channels with disciplined execution. Here's how:
          </p>
          
          <div className="space-y-6">
            {framework.map((phase, index) => (
              <div key={index} className="bg-gradient-to-br from-white to-gray-50 border-2 border-gray-200 rounded-2xl p-8 hover:border-[#38A3A5] transition-all">
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-12 h-12 bg-[#38A3A5] rounded-xl flex items-center justify-center flex-shrink-0">
                    <span className="text-white font-bold text-xl">{index + 1}</span>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold text-[#000000] mb-2">{phase.phase}</h3>
                    <p className="text-[#38A3A5] font-semibold mb-4">Timeline: {phase.duration}</p>
                  </div>
                </div>
                <ul className="space-y-3 ml-16">
                  {phase.activities.map((activity, idx) => (
                    <li key={idx} className="flex items-start gap-3">
                      <CheckCircle size={20} className="text-[#38A3A5] flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">{activity}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        {/* What Makes Us Different */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold mb-4 text-[#000000]">What Makes Us Different</h2>
          <p className="text-lg text-gray-700 mb-8">
            Market centers are relationship businesses. Empty showrooms don't generate orders—strategic positioning, buyer relationships, and professional representation do. That's where we excel.
          </p>
          
          <div className="grid md:grid-cols-2 gap-6">
            {differentiators.map((item, index) => (
              <div key={index} className="bg-white border-2 border-gray-200 rounded-2xl p-8 hover:border-[#38A3A5] transition-all">
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-[#38A3A5] to-[#38A3A5] rounded-xl flex items-center justify-center flex-shrink-0">
                    <item.icon size={24} className="text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-[#000000]">{item.title}</h3>
                </div>
                <p className="text-gray-700">{item.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Results Section */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold mb-8 text-[#000000]">Proven Results</h2>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-gradient-to-br from-[#38A3A5] to-[#38A3A5] rounded-2xl p-8 text-white">
              <div className="text-5xl font-bold mb-2">$2.1M</div>
              <div className="text-lg opacity-90">Average annual orders per showroom</div>
            </div>
            <div className="bg-gradient-to-br from-[#38A3A5] to-[#38A3A5] rounded-2xl p-8 text-white">
              <div className="text-5xl font-bold mb-2">280+</div>
              <div className="text-lg opacity-90">Buyer meetings per market week</div>
            </div>
            <div className="bg-gradient-to-br from-[#38A3A5] to-[#38A3A5] rounded-2xl p-8 text-white">
              <div className="text-5xl font-bold mb-2">34%</div>
              <div className="text-lg opacity-90">Showroom visitor to order conversion rate</div>
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="bg-gradient-to-br from-[#38A3A5] to-[#38A3A5] rounded-2xl p-12 text-center text-white">
          <h2 className="text-4xl font-bold mb-4">Ready to Enter Market Centers Strategically?</h2>
          <p className="text-xl mb-8 opacity-90">
            Let's discuss which market centers align with your buyer targets and how we can maximize your showroom ROI.
          </p>
          <a 
            href="mailto:contact@motifunbound.com?subject=Schedule%20a%20Consult&body=Hi%20Motif%20Unbound%20team,%0D%0A%0D%0AI'd%20like%20to%20schedule%20a%20consultation%20to%20discuss%20our%20market%20expansion.%20Please%20share%20available%20times.%0D%0A%0D%0AThank%20you."
            className="inline-block bg-white text-[#38A3A5] px-8 py-4 rounded-xl font-semibold text-lg hover:bg-gray-100 transition-all"
          >
            Schedule a Consult
          </a>
        </div>
      </div>
    </div>
  );
}
